import { useEffect, useState, useRef } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import api from "@/lib/api";

const MESSAGES = {
  degraded: "Estamos recuperando algunos servicios. La app sigue funcionando con normalidad.",
  down: "Estamos restableciendo el servicio. Reintentaremos automáticamente en unos segundos.",
};

// Páginas públicas/marketing donde NO se muestra el banner de estado interno.
const PUBLIC_PREFIXES = ["/login", "/admin/login", "/planes", "/comparativa", "/verificar"];

function isPublicRoute(pathname) {
  if (pathname === "/") return true;
  return PUBLIC_PREFIXES.some((p) => pathname.startsWith(p));
}

// Solo mostramos "caído" tras varias fallas seguidas (evita falsas alarmas por parpadeos).
const FAIL_THRESHOLD = 3;

export const HealthBanner = () => {
  const location = useLocation();
  const [status, setStatus] = useState("ok");
  const failures = useRef(0);
  const onPublic = isPublicRoute(location.pathname);

  useEffect(() => {
    // No consultamos ni mostramos nada en páginas públicas.
    if (onPublic) {
      setStatus("ok");
      failures.current = 0;
      return;
    }
    let active = true;
    const check = async () => {
      try {
        const { data } = await api.get("/api/health/db", { __noRetry: true });
        if (!active) return;
        failures.current = 0;
        setStatus(data.status || "ok");
      } catch {
        if (!active) return;
        failures.current += 1;
        if (failures.current >= FAIL_THRESHOLD) setStatus("down");
      }
    };
    check();
    const id = setInterval(check, 30000);
    return () => {
      active = false;
      clearInterval(id);
    };
  }, [onPublic]);

  if (onPublic || status === "ok") return null;

  return (
    <div
      className={`fixed top-0 inset-x-0 z-[60] text-white text-sm font-medium px-4 py-2.5 flex items-center justify-center gap-2 shadow-lg ${
        status === "down" ? "bg-[#EF4444]" : "bg-[#F59E0B]"
      }`}
      data-testid="health-banner"
      role="status"
    >
      <AlertTriangle size={16} className="shrink-0" />
      <span className="text-center">{MESSAGES[status] || MESSAGES.degraded}</span>
    </div>
  );
};
